import React, { Component } from 'react';
import { Context } from 'context';

import clone from 'clone';
import segment from "segment";

import Cell from 'editor/workspace/window/viewer/sheet/Cell';

import styles from 'editor/workspace/window/viewer/sheet/sheet.module.scss';

class Sheet extends Component {
    static contextType = Context;

    state = {
        start: false,
        end: false,
        width: 0,
        point: null,
        y: null,
        value: null,
        copy: null,
        selectionRevision: 0 //passed to tables as key prop every selection change
    };

    /**
     * Set the selected corner point and prepare the move and up event listeners
     * @param {Object} event
     */
    setPoint = (point) => {
        segment.track("project_edited", {
            application: "editor",
            type: "dragPoint",
            projectId: this.context.id,
            mapId: this.props.window.mapId,
            axisId: this.props.table.id,
            projectType: this.context.metadata.projectType === "DEMO" ? "demo" : "user",
        })

        window.addEventListener("mousemove", this.movePoint);
        window.addEventListener("touchmove", this.movePoint);
        window.addEventListener("mouseup", (e) => {
            this.props.save(this.props.table);

            this.setState({
                point: null,
                y: null,
                value: null
            });

            this.props.updateFormula("");

            window.removeEventListener("mousemove", this.movePoint);
        }, { once: true });

        window.addEventListener("touchend", (e) => {
            this.props.save(this.props.table);

            this.setState({
                point: null,
                y: null,
                value: null
            });

            this.props.updateFormula("");

            window.removeEventListener("touchmove", this.movePoint);
        }, { once: true });

        this.setState({
            point: point,
            copy: clone(this.props.table)
        })
    };

    /**
     * Calculate movement and apply a gradient
     * @param {Object} event
     */
    movePoint = (event) => {
        let movement = 0;
        let y = event.y;
        if(event.touches) y = event.touches[0].clientY;

        if(this.state.y){
            movement = this.state.y - y;
        }

        const value = this.state.value + (movement / 200);

        if(["top", "right", "left", "bottom"].includes(this.state.point)){
            this.props.applyEdgeGradient(this.state.copy, this.state.point, value);
        } else {
            this.props.applyCornerGradient(this.state.copy, this.state.point, value);
        }

        this.setState({
            y,
            value
        })
    };

    /**
     * Set start point on mouse down and select that cell
     * @param {Object} event
     */
    startSelection = (event) => {
        if(event.target.classList.contains("cell")){
            let start = { i: parseInt(event.target.dataset.i), j: parseInt(event.target.dataset.j)};

            this.setState({ start });
            this.props.setSelection(start, start);
        } else if (event.target.classList.contains("xLabel")) {
            let start = {i: 0, j: parseInt(event.target.dataset.j)};
            let end = {i: this.props.table.dataAxis.matrix.shape.m - 1, j: parseInt(event.target.dataset.j)};

            this.setState({ start, end });
            this.props.setSelection(start, end);
        } else if (event.target.classList.contains("yLabel")) {
            let start = {i: parseInt(event.target.dataset.i), j: 0};
            let end = {i: parseInt(event.target.dataset.i), j: this.props.table.dataAxis.matrix.shape.n - 1};

            this.setState({ start, end });
            this.props.setSelection(start, end);
        }
    };

    /**
     * Calculate and apply selection on mouse / touch move
     * @param {Object} event
     */
    calculateSelection = (event) => {
        let mouseIsDown = event.buttons;

        // Determine if event is triggered by mouse or touch and set target accordingly
        let target = event.target;
        if(event.touches) target = document.elementFromPoint(event.touches[0].clientX, event.touches[0].clientY);
        if(event.touches) mouseIsDown = true;

        let mouseHasChangedCell = this.state.end.i !== parseInt(target.dataset.i) || this.state.end.j !== parseInt(target.dataset.j);

        if(target && !this.state.point && mouseIsDown && this.state.start && mouseHasChangedCell) {
            if (event.target.classList.contains("cell")) {
                let end = {
                    i: parseInt(target.dataset.i), j: parseInt(target.dataset.j)
                };

                //hack to re-render whole table when dimension is less than 2. Fixes css border issues for selected cells
                if (this.props.table.dimension <= 2) this.setState({selectionRevision: this.state.selectionRevision + 1});

                this.setState({start: this.state.start, end});
                this.props.setSelection(this.state.start, end);
            } else if (event.target.classList.contains("xLabel")) {
                let start = {i: 0, j: this.state.start.j};
                let end = {i: this.props.table.dataAxis.matrix.shape.m - 1, j: parseInt(target.dataset.j)};

                this.setState({start, end});
                this.props.setSelection(start, end);
            } else if (event.target.classList.contains("yLabel")) {
                let start = {i: this.state.start.i, j: 0};
                let end = {i: parseInt(event.target.dataset.i), j: this.props.table.dataAxis.matrix.shape.n - 1};

                this.setState({ start, end });
                this.props.setSelection(start, end);
            }
        }
    };

    /**
     * Reset start and end points on mouse / touch up
     */
    endSelection = (event) => {
        this.props.endSelection();
        if (event.target.classList.contains("cell")) {
            this.setState({ end: false })
        } else {
            this.setState({ end: false, start: false })
        }
    };

    render() {
        const table = this.props.table;

        // Define and format table data
        const dataAxis = table.dataAxis.matrix.formatData();
        const xAxis = table.xAxis?.matrix.formatData().reduce((acc, val) => acc.concat(val), []);
        const yAxis = table.yAxis?.matrix.formatData().reduce((acc, val) => acc.concat(val), []);

        // Prepare calculations for heatMap
        const flat = dataAxis.reduce((acc, val) => acc.concat(val), []);
        const h = Math.max(...flat);
        const l = Math.min(...flat);
        const range = h - l;
        const step = range / 4;

        return (
            <div className={`highlightSecondary ${styles.sheet} ${table.dimension === 3 && styles.padded} ${table.dimension >= 1 && styles.scrollable}`} id="sheet">
                <div className={styles.wrapper}
                     onMouseDown={this.startSelection}
                     onMouseMove={this.calculateSelection}
                     onMouseUp={this.endSelection}
                     onMouseLeave={this.endSelection}
                     onTouchStart={this.startSelection}
                     onTouchMove={this.calculateSelection}
                     onTouchEnd={this.endSelection}
                >
                    <table ref={this.table}>
                        <thead>
                            <tr>
                                {table.yAxis &&
                                    <th
                                        scope="col"
                                        rowspan="2"
                                        colspan="2"
                                        title="Select All (Ctrl/Cmd + a)"
                                        className={`${styles.selectAll} highlightSecondary`}
                                        onClick={this.props.selectAll}
                                        data-hint={this.context.text.hints.selectAll}
                                    ><span/>
                                    </th>
                                }
                                {table.xAxis &&
                                    <th
                                        colspan="100%"
                                        className={`highlightSecondary ${styles.xAxisLabel} ${table.xAxis.readOnly ? styles.readOnly : null}`}
                                        title={table.xAxis.formattedName}
                                        onClick={!table.xAxis.readOnly ? this.props.activateAxisEditing.bind(this, "xAxis") : null}
                                        data-hint={this.context.text.hints.openAxisEditing}
                                    >
                                        {table.xAxis.formattedName}
                                        <img src={require('images/icons/axisArrow-dark.svg')} alt="Arrow"/>
                                    </th>
                                }
                            </tr>
                            <tr
                                data-hint={this.context.text.hints.sheet}
                            >
                                {xAxis?.map((value, j) =>
                                    <th
                                        key={j}
                                        scope="col"
                                        data-j={j}
                                        className={`xLabel highlightSecondary ${table.xAxis.matrix.values[0][j] !== this.props.uploaded.xAxis.matrix.values[0][j] ? styles.changed : ""}`}
                                    >
                                        {value}
                                    </th>
                                )}
                            </tr>
                        </thead>

                        <tbody
                            key={this.state.selectionRevision} /*if key changes, table is forced to re-render*/
                            data-hint={this.context.text.hints.sheet}
                        >
                            {dataAxis.map((row, i) =>
                                <tr key={`tr-${i}`}>
                                    {i === 0 && table.yAxis &&
                                        <th rowspan="100%"
                                            className={`highlightSecondary ${styles.yAxisLabel} ${table.yAxis.readOnly ? styles.readOnly : null}`}
                                            title={table.yAxis.formattedName}
                                            onClick={!table.yAxis.readOnly ? this.props.activateAxisEditing.bind(this, "yAxis") : null}
                                            data-hint={this.context.text.hints.openAxisEditing}
                                        >
                                            <div>
                                                {table.yAxis.formattedName}
                                                <img src={require('images/icons/axisArrow-dark.svg')} alt="Arrow"/>
                                            </div>
                                        </th>
                                    }

                                    {table.yAxis ?
                                        <th scope="row" data-i={i} className={`yLabel highlightSecondary ${table.yAxis.matrix.values[0][i] !== this.props.uploaded.yAxis.matrix.values[0][i] ? styles.changed : null}`}>
                                            {yAxis[i]}
                                        </th>
                                    : null}

                                    {row.map((cell, j) =>
                                        <Cell
                                            key={`cell-${i}-${j}`}
                                            cell={cell}
                                            i={i}
                                            j={j}
                                            step={step}
                                            range={range}
                                            l={l}
                                            selection={this.props.selection}
                                            uploaded={this.props.uploaded.dataAxis}
                                            point={this.state.point}
                                            value={this.state.value}
                                            setPoint={this.setPoint}
                                            diffType={this.props.diffType}
                                        />
                                    )}
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        )
    }
}

export default Sheet;